// Chip descriptors for the "active filters" row rendered above a FilterControls panel (KpiBuilder,
// SpsBuilder). Pure and i18n-free: callers hand over their own already-translated group labels plus the
// matching control's getFullValues() output, so nothing here needs to know about the filter definitions.
//
// `valueKey` must match the control's own `titleKey` (a select declared with `titleKey: 'companyName'`
// carries no `name`, and the fallback chain below would otherwise fall through to the raw id).
export function buildFilterChips(entries = []) {
	return (Array.isArray(entries) ? entries : []).reduce((chips, entry) => {
		const chip = buildFilterChip(entry);
		if (chip) {
			chips.push(chip);
		}

		return chips;
	}, []);
}

// Brings the field a chip stands for into view, for the "click the chip to go edit it" gesture. The
// builders can be mounted inside the legacy iframe, where this module's global `document` is the OUTER
// one and would never find the field: resolve it through the overlay root's ownerDocument when there is
// one. The id is the wrapper FilterField.vue puts on every field.
export function scrollFilterFieldIntoView(filterKey, overlayTarget = null) {
	if (!filterKey) {
		return;
	}

	const targetDocument = overlayTarget?.ownerDocument || document;
	targetDocument.getElementById(`filter-field-${filterKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

// Aggregates at 2+ selections (RG-09: "Catégorie: 2 valeurs"), not just >2 — a single value is still
// spelled out, everything from 2 up shows a count. `kind` distinguishes value/aggregated/state chips
// (all removable the same way — closing one resets the whole underlying control).
// `operator` overrides the label separator for a single-value chip (e.g. a threshold filter rendered as
// "Freshness > 5" instead of the default "Freshness: 5"); it never applies to the aggregated N+ form.
function buildFilterChip({ key, label, values, valueKey = null, active = null, operator = null } = {}) {
	if (!key) {
		return null;
	}

	// A boolean toggle (e.g. "Exclure les organisations partagées") carries no value list — only show it once active.
	if (active !== null) {
		return active ? { key, kind: 'state', label, fullLabel: label } : null;
	}

	if (!Array.isArray(values) || values.length === 0) {
		return null;
	}

	const displayValues = values
		.map((value) => (valueKey ? (value?.[valueKey] ?? value?.name ?? value?.label ?? value?.value ?? value?.id ?? value) : value))
		.filter((value) => value !== null && value !== undefined && value !== '')
		.map((value) => String(value));

	if (!displayValues.length) {
		return null;
	}

	return {
		key,
		kind: displayValues.length > 1 ? 'aggregated' : 'value',
		label: displayValues.length > 1 ? `${label}: ${displayValues.length}` : operator ? `${label} ${operator} ${displayValues[0]}` : `${label}: ${displayValues[0]}`,
		fullLabel: `${label}: ${displayValues.join(', ')}`,
	};
}
